import { useEffect, useMemo } from 'react'
import moment from 'moment'
import {
  useDashboardFetchWaves,
  useReportDashboardFetchWave,
  waveFetchState,
} from '@context/DashboardFetchWaveContext'
import { earliestWave } from '@context/dashboardFetchWave'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  buildDashboardDetailHref,
  DASHBOARD_DETAIL_CONTEXT,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import { FINANCE_PERIOD_RIGHT_LABEL } from '@constants/Dashboard/financeDashboard.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useARAgingOverview } from '@hooks/dashboard/useARAging'
import { useAROver90Overview } from '@hooks/dashboard/useAROver90'
import { useCollectionOverview } from '@hooks/dashboard/useCollection'
import { useTreatmentValueOverview } from '@hooks/dashboard/useTreatmentValue'
import { usePracticeRevenueOverview } from '@hooks/dashboard/usePracticeRevenue'
import { useClaimsOverview } from '@hooks/dashboard/useClaims'
import { useOutstandingBalanceOverview } from '@hooks/dashboard/useOutstandingBalance'
import {
  FINANCE_PRODUCTION_BY_PROCEDURE_CARD_CATEGORY_LIMIT,
  useFinanceProductionByProcedureOverview,
} from '@hooks/dashboard/useFinanceProductionByProcedure'
import { useFinanceProductionPerProviderOverview } from '@hooks/dashboard/useFinanceProductionPerProvider'
import { useProductionByDepartmentOverview } from '@hooks/dashboard/useProductionByDepartment'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
} from '@hooks/dashboard/useDashboardPeriod'
import {
  getHygieneProductionOverview,
  getProductionOverview,
  type ProductionDashboardResult,
} from '@services/api/dashboard/finance.api'
import {
  CALENDAR_DATE_FORMAT,
  computeChartAxis,
  filterYtdPeriodItems,
  formatChartCurrencyKUnits,
  goalDollarsToChartUnits,
  formatGoalCurrencyLabel,
  formatPercent,
} from '@utils/dashboardFinance.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { useClinicGoal } from '@hooks/dashboard/useClinicGoal'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import {
  buildNetHygieneProductionKpis,
  buildProductionPerVisitHygieneKpis,
} from '@utils/hygieneDashboard.util'
import { createFailedToast } from '@utils/notificationToast.util'
import {
  buildFinanceSectionWaves,
  type FinanceSectionWaves,
} from './financeSectionWaves'

export type { FinanceSectionWaves } from './financeSectionWaves'

type UseFinanceDashboardOptions = {
  range: RangeKey
  date: string
  hiddenSections: Set<string>
  providerSearch: string
}

function loadingOnWave(
  wave: number,
  sectionWave: number | null,
  loading: boolean,
): boolean {
  return sectionWave === wave && loading
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- inferred; exported hook is typed below
function useFinanceDashboardImpl({
  range,
  date,
  hiddenSections,
  providerSearch,
}: UseFinanceDashboardOptions) {
  const waves: FinanceSectionWaves = buildFinanceSectionWaves(hiddenSections)
  const periodLabel = FINANCE_PERIOD_RIGHT_LABEL[range]
  const openWaves = useDashboardFetchWaves()
  const practiceRevenueGate = waveFetchState(openWaves, waves.practiceRevenue)
  const productionGate = waveFetchState(openWaves, waves.production)
  const collectionGate = waveFetchState(openWaves, waves.collection)
  const productionByDepartmentGate = waveFetchState(
    openWaves,
    waves.productionByDepartment,
  )
  const treatmentValueGate = waveFetchState(openWaves, waves.treatmentValue)
  const arAgingGate = waveFetchState(openWaves, waves.arAging)
  const outstandingGate = waveFetchState(openWaves, waves.outstanding)
  const claimsGate = waveFetchState(openWaves, waves.claims)
  const hygieneGate = waveFetchState(openWaves, waves.hygiene)
  const arOver90Gate = waveFetchState(openWaves, waves.arOver90)
  const productionByProcedureGate = waveFetchState(
    openWaves,
    waves.productionByProcedure,
  )
  const productionPerProviderGate = waveFetchState(
    openWaves,
    waves.productionPerProvider,
  )
  const clinicGoalGate = waveFetchState(
    openWaves,
    earliestWave([
      waves.practiceRevenue,
      waves.production,
      waves.collection,
      waves.hygiene,
    ]),
  )

  const hrefs = useMemo(() => {
    const detailParams = {
      context: DASHBOARD_DETAIL_CONTEXT.finance,
      range,
      date: moment(date, CALENDAR_DATE_FORMAT).format('YYYY-MM-DD'),
    }
    return {
      practiceRevenueOverview: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.practiceRevenueOverview,
        detailParams,
      ),
      production: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.production,
        detailParams,
      ),
      collectionPerformance: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.collectionPerformance,
        detailParams,
      ),
      insurancePatientCollection: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.insurancePatientCollection,
        detailParams,
      ),
      dentistHygienistProduction: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.dentistHygienistProduction,
        detailParams,
      ),
      treatmentValue: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.treatmentValue,
        detailParams,
      ),
      arAging: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.arAging,
        detailParams,
      ),
      outstandingBalance: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeOutstandingBalance,
        detailParams,
      ),
      financeClaims: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeClaims,
        detailParams,
      ),
      financeCollection: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeCollection,
        detailParams,
      ),
      financeCollectionRatio: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeCollectionRatio,
        detailParams,
      ),
      financeProductionByDepartment: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeProductionByDepartment,
        detailParams,
      ),
      netHygieneProduction: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.netHygieneProduction,
        detailParams,
      ),
      productionPerHygieneVisit: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.productionPerHygieneVisit,
        detailParams,
      ),
      arOver90Day: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.arOver90Day,
        detailParams,
      ),
      productionByProcedure: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeProductionByProcedure,
        detailParams,
      ),
      productionPerProvider: buildDashboardDetailHref(
        DASHBOARD_DETAIL_HREFS.financeProductionPerProvider,
        detailParams,
      ),
    }
  }, [range, date])

  const arAgingApi = useARAgingOverview({
    range,
    date,
    enabled: arAgingGate.enabled,
    queued: arAgingGate.queued,
  })
  const arOver90Api = useAROver90Overview({
    range,
    date,
    enabled: arOver90Gate.enabled,
    queued: arOver90Gate.queued,
  })
  const collectionApi = useCollectionOverview({
    range,
    date,
    enabled: collectionGate.enabled,
    queued: collectionGate.queued,
  })
  const treatmentValueApi = useTreatmentValueOverview({
    range,
    date,
    enabled: treatmentValueGate.enabled,
    queued: treatmentValueGate.queued,
  })
  const practiceRevenueApi = usePracticeRevenueOverview({
    range,
    date,
    enabled: practiceRevenueGate.enabled,
    queued: practiceRevenueGate.queued,
  })
  const claimsApi = useClaimsOverview({
    range,
    date,
    enabled: claimsGate.enabled,
    queued: claimsGate.queued,
  })
  const outstandingApi = useOutstandingBalanceOverview({
    range,
    date,
    enabled: outstandingGate.enabled,
    queued: outstandingGate.queued,
  })
  const prodByProcedureApi = useFinanceProductionByProcedureOverview({
    range,
    date,
    maxChartCategories: FINANCE_PRODUCTION_BY_PROCEDURE_CARD_CATEGORY_LIMIT,
    enabled: productionByProcedureGate.enabled,
    queued: productionByProcedureGate.queued,
  })
  const prodPerProviderApi = useFinanceProductionPerProviderOverview({
    range,
    date,
    providerSearch,
    enabled: productionPerProviderGate.enabled,
    queued: productionPerProviderGate.queued,
  })
  const prodByDeptApi = useProductionByDepartmentOverview({
    range,
    date,
    enabled: productionByDepartmentGate.enabled,
    queued: productionByDepartmentGate.queued,
  })
  const { goal: clinicGoal, loading: clinicGoalLoading } = useClinicGoal({
    range,
    date,
    enabled: clinicGoalGate.enabled,
    queued: clinicGoalGate.queued,
  })

  const hygieneQueryParams = useDashboardPeriodParams(range, date)
  const {
    data: hygieneData,
    loading: hygieneLoading,
    error: hygieneError,
  } = useDashboardPeriodFetch(
    hygieneQueryParams,
    getHygieneProductionOverview,
    {
      enabled: hygieneGate.enabled,
      queued: hygieneGate.queued,
    },
  )

  const {
    data: productionData,
    loading: productionLoading,
    error: productionError,
  } = useDashboardPeriodFetch<ProductionDashboardResult>(
    hygieneQueryParams,
    getProductionOverview,
    { enabled: productionGate.enabled, queued: productionGate.queued },
  )

  const netHygieneMetric = useMemo(() => {
    const metric = buildNetHygieneProductionKpis(hygieneData)[0]
    if (!metric) return metric
    return {
      ...metric,
      rightLabel: formatGoalCurrencyLabel(
        clinicGoal?.net_hygiene_production_goal,
      ),
    }
  }, [hygieneData, clinicGoal])
  const productionPerVisitMetric = useMemo(() => {
    const metric = buildProductionPerVisitHygieneKpis(hygieneData)[0]
    if (!metric) return metric
    return {
      ...metric,
      rightLabel: formatGoalCurrencyLabel(
        clinicGoal?.production_per_hygiene_visit_goal,
      ),
    }
  }, [hygieneData, clinicGoal])

  const hygieneApi = {
    loading: hygieneLoading,
    error: hygieneError,
    hasPayload: hygieneData != null,
    hasNetHygieneData: hasNonZeroDashboardValues(
      hygieneData?.hygieneProduction.current,
    ),
    hasProductionPerVisitData: hasNonZeroDashboardValues(
      hygieneData?.productionPerHygieneVisit.current,
    ),
    netHygieneMetric,
    productionPerVisitMetric,
  }

  const prodPanel = useMemo(() => {
    const gross = productionData?.summary.grossProduction.current ?? 0
    const net = productionData?.summary.netProduction.current ?? 0
    const adjustmentPct =
      productionData?.summary.adjustmentPercentage.current ?? 0
    const ratePct = productionData?.summary.productionRate.current ?? 0

    const grossPctChange =
      productionData?.summary.grossProduction.percentChange ?? 0
    const prevGross = productionData?.summary.grossProduction.previous ?? 0
    const deltaAmt = gross - prevGross

    const changeSignedPct = `${grossPctChange >= 0 ? '+' : ''}${Math.abs(grossPctChange).toFixed(0)}%`
    const changeAmt = `$${Math.round(deltaAmt).toLocaleString('en-US')}`

    const productionRaw = productionData?.productionPeriodSummary ?? []
    const accountingRaw = productionData?.accountingPeriodSummary ?? []
    const paired = filterYtdPeriodItems(
      range,
      date,
      productionRaw.map((grossBucket, index) => ({
        gross: grossBucket,
        net: accountingRaw[index],
      })),
      (item) => item.gross.label,
    )

    const prodChart = paired.map(({ gross: grossBucket, net: netBucket }) => ({
      label: grossBucket.label,
      Gross: grossBucket.value / 1000,
      Net: netBucket?.value ? netBucket.value / 1000 : 0,
    }))

    const values = prodChart.flatMap((p) => [p.Gross, p.Net])
    const axis = computeChartAxis(values)

    const prodGoalLine = goalDollarsToChartUnits(
      clinicGoal?.gross_production_chart_goal,
    )
    const prodGoalLabel = formatGoalCurrencyLabel(
      clinicGoal?.gross_production_goal,
    )

    const collChart = prodChart
    return {
      prodGoalLabel,
      collGoalLabel: formatGoalCurrencyLabel(clinicGoal?.gross_collection_goal),
      changeSignedPct,
      changeAmt,
      gross: formatChartCurrencyKUnits(gross / 1000),
      net: formatChartCurrencyKUnits(net / 1000),
      adjustment: formatPercent(adjustmentPct, 1),
      rate: formatPercent(ratePct, 0),
      prodChart,
      prodYDomain: axis.yDomain,
      prodYTicks: axis.yTicks,
      prodGoalLine,
      collChart,
      collYDomain: axis.yDomain,
      collYTicks: axis.yTicks,
      collGoalLine: goalDollarsToChartUnits(clinicGoal?.collection_chart_goal),
    }
  }, [productionData, range, date, clinicGoal])

  const productionHasChartData = prodPanel.prodChart.some((point) =>
    hasNonZeroDashboardValues(point.Gross, point.Net),
  )

  const loading =
    arAgingApi.loading ||
    arOver90Api.loading ||
    collectionApi.loading ||
    treatmentValueApi.loading ||
    practiceRevenueApi.loading ||
    claimsApi.loading ||
    outstandingApi.loading ||
    prodByProcedureApi.loading ||
    prodPerProviderApi.loading ||
    prodByDeptApi.loading ||
    hygieneLoading ||
    productionLoading ||
    clinicGoalLoading

  const hasError =
    (!arAgingApi.loading && !!arAgingApi.error) ||
    (!arOver90Api.loading && !!arOver90Api.error) ||
    (!collectionApi.loading && !!collectionApi.error) ||
    (!treatmentValueApi.loading && !!treatmentValueApi.error) ||
    (!practiceRevenueApi.loading && !!practiceRevenueApi.error) ||
    (!claimsApi.loading && !!claimsApi.error) ||
    (!outstandingApi.loading && !!outstandingApi.error) ||
    (!prodByProcedureApi.loading && !!prodByProcedureApi.error) ||
    (!prodPerProviderApi.loading && !!prodPerProviderApi.error) ||
    (!prodByDeptApi.loading && !!prodByDeptApi.error) ||
    (!productionLoading && !!productionError) ||
    (!hygieneApi.loading && !!hygieneApi.error)

  const goalWave = earliestWave([
    waves.practiceRevenue,
    waves.production,
    waves.collection,
    waves.hygiene,
  ])
  useReportDashboardFetchWave(
    0,
    loadingOnWave(0, waves.practiceRevenue, practiceRevenueApi.loading) ||
      loadingOnWave(0, waves.production, productionLoading) ||
      loadingOnWave(0, waves.collection, collectionApi.loading) ||
      loadingOnWave(0, goalWave, clinicGoalLoading),
  )
  useReportDashboardFetchWave(
    1,
    loadingOnWave(1, waves.productionByDepartment, prodByDeptApi.loading) ||
      loadingOnWave(1, waves.treatmentValue, treatmentValueApi.loading) ||
      loadingOnWave(1, waves.arAging, arAgingApi.loading) ||
      loadingOnWave(1, waves.outstanding, outstandingApi.loading) ||
      loadingOnWave(1, waves.claims, claimsApi.loading) ||
      loadingOnWave(1, waves.collection, collectionApi.loading) ||
      loadingOnWave(1, goalWave, clinicGoalLoading),
  )
  useReportDashboardFetchWave(
    2,
    loadingOnWave(2, waves.hygiene, hygieneLoading) ||
      loadingOnWave(2, waves.arOver90, arOver90Api.loading) ||
      loadingOnWave(
        2,
        waves.productionByProcedure,
        prodByProcedureApi.loading,
      ) ||
      loadingOnWave(
        2,
        waves.productionPerProvider,
        prodPerProviderApi.loading,
      ) ||
      loadingOnWave(2, waves.productionByDepartment, prodByDeptApi.loading) ||
      loadingOnWave(2, waves.collection, collectionApi.loading) ||
      loadingOnWave(2, waves.arAging, arAgingApi.loading) ||
      loadingOnWave(2, goalWave, clinicGoalLoading),
  )

  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (!loading && hasError) {
      showToast(
        createFailedToast(
          'Failed to load metric',
          'Failed to load finance dashboard data. Please try again.',
        ),
      )
    }
  }, [loading, hasError, showToast])

  return {
    range,
    periodLabel,
    hrefs,
    loading,
    hasError,
    prodPanel,
    productionLoading,
    productionHasPayload: productionData != null,
    productionHasChartData,
    productionHasData:
      hasNonZeroDashboardValues(
        productionData?.summary.grossProduction.current,
        productionData?.summary.netProduction.current,
        productionData?.summary.adjustmentPercentage.current,
        productionData?.summary.productionRate.current,
      ) || productionHasChartData,
    arAgingApi,
    arOver90Api,
    collectionApi,
    treatmentValueApi,
    practiceRevenueApi,
    claimsApi,
    outstandingApi,
    prodByProcedureApi,
    prodPerProviderApi,
    prodByDeptApi,
    hygieneApi,
  }
}

export type FinanceDashboard = ReturnType<typeof useFinanceDashboardImpl>

export function useFinanceDashboard(
  options: UseFinanceDashboardOptions,
): FinanceDashboard {
  return useFinanceDashboardImpl(options)
}

export type HygieneApiData = {
  loading: boolean
  error: string | null
  hasPayload: boolean
  hasNetHygieneData: boolean
  hasProductionPerVisitData: boolean
  netHygieneMetric: DashboardMetricItemData | undefined
  productionPerVisitMetric: DashboardMetricItemData | undefined
}
